import '/node_modules/flag-icons/css/flag-icons.min.css';

import IconDirectDefault from '@assets/header/default.svg';
import IconDirectEmail from '@assets/header/email.svg';
import IconDirectInstagram from '@assets/header/instagram.svg';
import IconDirectMessenger from '@assets/header/messanger.svg';
import IconDirectPhone from '@assets/header/phone.svg';
import IconDirectTelegram from '@assets/header/telegram.svg';
import IconDirectViber from '@assets/header/viber.svg';
import IconDirectWhatsApp from '@assets/header/whatsapp.svg';
import {
  IconDirectEmailSVG,
  IconDirectInstagramSVG,
  IconDirectMessengerSVG,
  IconDirectPhoneSVG,
  IconDirectTelegramSVG,
  IconDirectViberSVG,
  IconDirectWhatsAppSVG,
} from '@components/preview/components/Generics/Icons';
import { getIconTitle, hexToRgbA } from '@components/preview/utils/functions.js';
import * as S from '@styles/styled.js';
import { STORAGE_KEYS } from '@utils/constants.js';
import httpClient from '@utils/httpClient.js';
import { getTablerIconSync, loadTablerIcons } from '@utils/tablerIcons.js';
import { clearTokens, getToken } from '@utils/tokenStorage';
import Cookies from 'js-cookie';
import moment from 'moment';
import React from 'react';
import { createSelector } from 'reselect';

import {
  IconAlignCenter,
  IconAlignLeft,
  IconAlignRight,
  IconAvaliableSVG,
  IconContactInstagramSVG,
  IconContactMessengerSVG,
  IconContactTelegramSVG,
  IconContactTwitterSVG,
  IconContactWhatsAppSVG,
  IconDocumentLinkSVG,
  IconEmailTypeSVG,
  IconLinkTypeSVG,
  IconPageLinkSVG,
  IconPhoneTypeSVG,
  IconPostLinkSVG,
  IconSkypeTypeSVG,
  IconSoldoutSVG,
  IconViberTypeSVG,
} from '../components/design/Icons';
import { UserContactIcon, UserContactItemLink, UserContactList } from '../components/preview/views/Unilink/styled';
import { deLang, enLang, esLang, frLang, itLang, ptLang, trLang, uaLang } from '../components/previewUI/utils/translation';
import { Caption, CaptionContainer } from '../styles/Typography';
import { buttonLinkUrlCaptions, buttonLinkUrlType, statusTypes } from './enums';
import { BaseUrl } from './httpClient';
import { getUnlinkItemMap, getYouTubeMediaItemMap } from './mapper';
import { defaultValues } from './sectionEnums';
import { sectionTypes } from './sectionEnums';
import { deLangDashboard } from './translationDE.js';
import { enLangDashboard } from './translationEN.js';
import { esLangDashboard } from './translationES.js';
import { frLangDashboard } from './translationFR.js';
import { huLangDashboard } from './translationHU.js';
import { itLangDashboard } from './translationIT.js';
import { jaLangDashboard } from './translationJA.js';
import { plLangDashboard } from './translationPL.js';
import { ptLangDashboard } from './translationPT.js';
import { trLangDashboard } from './translationTR.js';
import { uaLangDashboard } from './translationUA.js';
import { zhLangDashboard } from './translationZH.js';

export const generateRandomUsername = () => {
  const randomDigits = Math.floor(Math.random() * 1e10)
    .toString()
    .padStart(10, '0');
  return 'user' + randomDigits;
};

export function getFileExtension(filename) {
  const parts = filename.split('.');
  return parts.length > 1 ? parts.pop() : '';
}

export const formattedNumber = (number) => {
  const value = Number(number);
  return Number.isNaN(value) ? 0 : new Intl.NumberFormat('en-IN', { maximumSignificantDigits: 3 }).format(value);
};

// Uploaded audio keeps its storage filename (`1727446633_klavdia-petrivna-zvernennya.mp3`),
// so it is cleaned up before being shown as a track name.
export const formatTrackTitle = (rawTitle) => {
  if (!rawTitle) return '';
  const words = String(rawTitle)
    .split('?')[0]
    .split('/')
    .pop()
    .replace(/\.(mp3|m4a|mp4|wav|ogg|oga|opus|aac|flac|webm)$/i, '')
    .replace(/^\d{8,}[-_\s]*/, '')
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!words) return '';
  return words
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const formatTrackDuration = (seconds) => {
  if (!seconds || !isFinite(seconds)) return '';
  const minutes = Math.floor(seconds / 60);
  const rest = Math.floor(seconds % 60);
  return `${minutes}:${rest.toString().padStart(2, '0')}`;
};

export const createCustomSelector = (type) => {
  const selectState = (state) => {
    return state[type];
  };
  return createSelector(selectState, (data) => ({
    headingTitle: data?.headingTitle ?? defaultValues.string,
    headingDescription: data?.headingDescription ?? defaultValues.string,
    headingTextAlign: data?.headingTextAlign ?? defaultValues.string,
    headingImage: data?.headingImage ?? defaultValues.string,
    headingDivider: data?.headingDivider ?? defaultValues.string,
    sectionIsBackground: data?.sectionIsBackground ?? defaultValues.false,
    sectionTextColor: data?.sectionTextColor ?? defaultValues.string,
    sectionBackgroundColor: data?.sectionBackgroundColor ?? defaultValues.string,
    sectionOpacity: data?.sectionOpacity ?? defaultValues.number,
    sectionPadding: data?.sectionPadding ?? defaultValues.sectionPadding,
    sectionMargin: data?.sectionMargin ?? defaultValues.sectionMargin,
    sectionIsFullWidth: data?.sectionIsBackground ? data?.sectionIsFullWidth : defaultValues.boolTrue,
    sectionShadow: data?.sectionIsBackground ? data?.sectionShadow : defaultValues.shadow,
    sectionShadowColor: data?.sectionShadowColor ?? defaultValues.string,
    sectionButtonBackgroundColor: data?.sectionButtonBackgroundColor ?? defaultValues.string,
    sectionButtonTextColor: data?.sectionButtonTextColor ?? defaultValues.string,
    sectionBackgroundImage: data?.sectionBackgroundImage ?? defaultValues.string,
    sectionBackgroundType: data?.sectionBackgroundType ?? defaultValues.number,
    sectionGradientColor1: data?.sectionGradientColor1 ?? defaultValues.gradientColor1,
    sectionGradientColor2: data?.sectionGradientColor2 ?? defaultValues.gradientColor2,
    sectionGradientDirection: data?.sectionGradientDirection ?? defaultValues.number,
    sectionGradientOffset: data?.sectionGradientOffset ?? defaultValues.number,
    sectionBorder: data?.sectionIsBackground ? data?.sectionBorder : defaultValues.border,
    sectionBorderColor: data?.sectionBorderColor ?? defaultValues.string,
    sectionShape: data?.sectionShape ?? defaultValues.shape,
    sectionIsShadow: data?.sectionIsShadow ?? defaultValues.bool,
    sectionIsShadowBorder: data?.sectionIsShadowBorder ?? defaultValues.bool,
    sectionShadowBorderColor: data?.sectionShadowBorderColor ?? defaultValues.string,
    itemTextColor: data?.itemTextColor ?? defaultValues.string,
    itemBackgroundColor: data?.itemBackgroundColor ?? defaultValues.string,
    itemShape: data?.itemShape ?? defaultValues.shape,
    itemBorder: data?.itemBorder ?? defaultValues.border,
    itemBorderColor: data?.itemBorderColor ?? defaultValues.blackColor,
    itemShadow: data?.itemShadow ?? defaultValues.string,
    itemShadowColor: data?.itemShadowColor ?? defaultValues.string,
    itemOpacity: data?.itemOpacity ?? defaultValues.number,
    buttonText: data?.buttonText ?? defaultValues.string,
    buttonTextColor: data?.buttonTextColor ?? defaultValues.string,
    buttonBackgroundColor: data?.buttonBackgroundColor ?? defaultValues.string,
    buttonOpacity: data?.buttonOpacity ?? defaultValues.number,
    buttonShape: data?.buttonShape ?? defaultValues.shape,
    buttonBorder: data?.buttonBorder ?? defaultValues.border,
    buttonBorderColor: data?.buttonBorderColor ?? defaultValues.blackColor,
    buttonShadow: data?.buttonShadow ?? defaultValues.shadow,
    buttonShadowColor: data?.buttonShadowColor ?? defaultValues.blackColor,
    buttonShadowBorderColor: data?.buttonShadowBorderColor ?? defaultValues.blackColor,
  }));
};

export const capitalize = (string) => {
  if (typeof string !== 'string') {
    return '';
  }

  return string?.charAt(0).toUpperCase() + string?.slice(1);
};

export function updateMediaItem(updatedMedia, list, setList) {
  if (Object.prototype.hasOwnProperty.call(updatedMedia ?? {}, 'id')) {
    const updatedList = list.map((el) => {
      if (el.id === updatedMedia.id) {
        return updatedMedia;
      } else {
        return el;
      }
    });

    setList(updatedList);
  }
}

export const getSectionFields = (data) => {
  if (!data) {
    return {};
  }

  return {
    ...data.blockDesign,
    sectionIsBackground: data?.blockDesign?.isSectionBackground,
    sectionIsFullWidth: data?.blockDesign?.isSectionBackground ? data?.blockDesign?.sectionIsFullWidth : defaultValues.boolTrue,
    sectionShadow: data?.blockDesign?.isSectionBackground ? data?.blockDesign?.sectionShadow : defaultValues.shadow,
    sectionBorder: data?.blockDesign?.isSectionBackground ? data?.blockDesign?.sectionBorder : defaultValues.border,
  };
};

export const capitalizeAllFields = (obj) => {
  if (!obj) {
    return {};
  }
  const newObj = {};
  Object.keys(obj).forEach((key) => {
    if (key.includes('cta')) {
      return (newObj[key[0].toUpperCase() + key[1].toUpperCase() + key[2].toUpperCase() + key.slice(3)] = obj[key]);
    }
    newObj[key[0].toUpperCase() + key.slice(1)] = obj[key];
  });
  return newObj;
};

export const getSectionBackground = (
  sectionIsBackground,
  sectionBackgroundType,
  sectionBackgroundColor,
  sectionOpacity,
  sectionBackgroundImage,
  sectionGradientColor1,
  sectionGradientColor2,
  sectionGradientDirection,
  sectionGradientOffset,
) => {
  if (!sectionIsBackground) {
    return 'none';
  } else if (sectionBackgroundType === 1) {
    return hexToRgbA(sectionBackgroundColor, (100 - sectionOpacity) / 100);
  } else if (sectionBackgroundType === 2) {
    return `linear-gradient(${sectionGradientDirection}deg, ${hexToRgbA(sectionGradientColor1, (100 - sectionOpacity) / 100)} ${sectionGradientOffset}%, ${hexToRgbA(
      sectionGradientColor2,
      (100 - sectionOpacity) / 100,
    )})`;
  } else if (sectionBackgroundType === 3) {
    return `url(${sectionBackgroundImage}) center center / cover no-repeat`;
  } else {
    return 'none';
  }
};

export const geProductType = (sectionType) => {
  let itemTitle = '';
  switch (sectionType) {
    case sectionTypes.blog.type:
      itemTitle = 'post';
      break;
    case sectionTypes.digital.type:
      itemTitle = 'product';
      break;
    case sectionTypes.links.type:
      itemTitle = 'link';
      break;
    case sectionTypes.gallery.type:
      itemTitle = 'photo';
      break;
    case sectionTypes.map.type:
      itemTitle = 'address';
      break;
    case sectionTypes.banner.type:
      itemTitle = 'media';
      break;
    case sectionTypes.coupons.type:
      itemTitle = 'product';
      break;
    case sectionTypes.nft.type:
      itemTitle = 'product';
      break;
    case sectionTypes.twitch.type:
      itemTitle = 'url or channel name';
      break;
    case sectionTypes.youtube.type:
      itemTitle = 'url';
      break;
    case sectionTypes.twitter.type:
      itemTitle = 'url';
      break;
    case sectionTypes.music.type:
      itemTitle = 'new source';
      break;
    case sectionTypes.requests.type:
      itemTitle = 'service';
      break;
    case sectionTypes.contacts.type:
      itemTitle = 'contact';
      break;
    case sectionTypes.html.type:
      itemTitle = 'code';
      break;
    case sectionTypes.subscribe.type:
      itemTitle = 'data';
      break;
    case sectionTypes.appointments.type:
      itemTitle = 'appointment';
      break;
    case sectionTypes.events.type:
      itemTitle = 'event';
      break;
    default:
      itemTitle = 'item';
      break;
  }
  return itemTitle;
};

export const px2vw = (size, width = 1440) => `${(size / width) * 100}vw`;

export function getSectionOpacity(opacity) {
  let sectionOpacityNumber = 0;

  if (opacity !== undefined && opacity !== null) {
    if (opacity < 1) {
      sectionOpacityNumber = 0;
    } else if (opacity > 100) {
      sectionOpacityNumber = 100;
    } else {
      sectionOpacityNumber = opacity;
    }
  }
  return sectionOpacityNumber;
}

export const getPageTitle = (id, pages) => {
  if (!pages.length) {
    return '';
  }
  const currentPage = pages?.find((el) => el?.id === id);
  if (!currentPage || !currentPage?.slug || currentPage?.slug === '/') {
    return '';
  }

  return `/${currentPage?.slug}`;
};

export function getFormBodyEncoded(body) {
  return Object.keys(body)
    .map((key) => {
      return encodeURIComponent(key) + '=' + encodeURIComponent(body[key]);
    })
    .join('&');
}

export function checkValidUrl(url) {
  return /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/.test(
    url,
  );
}

export function isValidUrl(url) {
  return /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/.test(
    url,
  );
}

export function isValidYouTubeLink(url) {
  return /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.?be)\/.+$/.test(url) || /^(https?:\/\/)?(www\.)?(m.youtube\.com|youtu\.?be)\/.+$/.test(url);
}

export function isValidEmail(email) {
  const reg = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return reg.test(String(email).toLowerCase());
}

export function checkIsValidEmail(email) {
  const reg = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return reg.test(String(email).toLowerCase());
}

export function getCodeFromYouTubeLink(url) {
  if (!url) {
    return '';
  }

  const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
  const match = url.match(regExp);

  if (match && match[7].length === 11) {
    return match[7];
  } else {
    const regExpMob = /^.*((m.youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExpMob);
    return match && match[7].length === 11 ? match[7] : false;
  }
}

export function getHeightTikTokVideo(width, isFourth = false) {
  let scrollSize = isFourth ? 24 : 33;
  let proportion = isFourth ? 20 : 50;

  let fullWidth = width - scrollSize;
  return Math.abs(((fullWidth / 100) * proportion - fullWidth) / 0.565);
}

export function isValidTimestamp(str) {
  const days = Number(`${str[0]}${str[1]}`);
  const hours = Number(`${str[3]}${str[4]}`);
  const minutes = Number(`${str[6]}${str[7]}`);

  return days <= 30 && hours <= 23 && minutes <= 59;
}

export function getParsedTimerTimestamp(str) {
  const days = Number(`${str[0]}${str[1]}`);
  const hours = Number(`${str[3]}${str[4]}`);
  const minutes = Number(`${str[6]}${str[7]}`);

  let currentTime = new Date();
  currentTime.setDate(currentTime.getDate() + days);
  currentTime.setHours(currentTime.getHours() + hours);
  currentTime.setMinutes(currentTime.getMinutes() + minutes);

  return currentTime;
}

export function checkContactMessengers(contacts) {
  return contacts.some((el) => el.type === 1 || el.type === 2 || el.type === 3 || el.type === 5);
}

export const getNavigatorLanguage = () =>
  navigator.languages && navigator.languages.length ? navigator.languages[0] : navigator.userLanguage || navigator.language || navigator.browserLanguage || 'en';

export const getGet = (name) => {
  let s = window.location.href;
  s = s.match(new RegExp(name + '=([^&=]+)'));
  return s ? s[1] : false;
};

export const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
};

export function clearAccountData() {
  localStorage.removeItem('unilinkProfileId');
  localStorage.removeItem('unilinkUserId');
}

export const logoutClearStore = () => {
  clearTokens();
  localStorage.removeItem('unilinkUserId');
  localStorage.removeItem('unilinkProfileId');
  localStorage.removeItem(STORAGE_KEYS.unilinkCloseSideBar);
  // const cookies = new Cookies();
  Cookies.remove('unilinkUserId');
  Cookies.remove('unilinkProfileId');
  Cookies.remove('.AspNet.Cookies');
  Cookies.remove('ARRAffinity');
  Cookies.remove('ARRAffinitySameSite');

  // document.cookie = 'unilinkUserId=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
};

export const deleteAccountClearStore = () => {
  localStorage.removeItem('unilinkAddNewSite');
  localStorage.removeItem('unilinkSubscribeTelegram');
  localStorage.removeItem('unilinkUSVisited');
  localStorage.removeItem('unilinkVisited');
  localStorage.removeItem('unilinkOpenToolsMenu');
  localStorage.removeItem('unilinkAddNewSite');
  localStorage.removeItem('unilinkMigrate');
  localStorage.removeItem('unilinkHideWelcomeMessage');
  localStorage.removeItem(STORAGE_KEYS.unilinkCloseSideBar);

  clearTokens();
  localStorage.removeItem('unilinkUserId');
  localStorage.removeItem('unilinkProfileId');

  // const cookies = new Cookies();
  Cookies.remove('unilinkUserId');
  Cookies.remove('unilinkProfileId');
  Cookies.remove('.AspNet.Cookies');
  Cookies.remove('ARRAffinity');
  Cookies.remove('ARRAffinitySameSite');

  // document.cookie = 'unilinkUserId=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  // document.cookie = 'ARRAffinity=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  // document.cookie = 'ARRAffinitySameSite=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
};

export function getParametersURL() {
  const url = window.location.href;
  const parameterPos = url.indexOf('?');
  const hash = url.slice(parameterPos + 1);

  return hash.split('&').reduce(function (res, item) {
    const parts = item.split('=');
    res[parts[0]] = parts[1];
    return res;
  }, {});
}

export function onChangeValue(e, setValue, saveValue, ...args) {
  const { value } = e.target;

  setValue(value, ...args);

  if (e.keyCode === 13 && saveValue) {
    saveValue();
  }

  // if (value.length < 512) {
  //   setValue(value);
  // }
}

export function onChangeText(e, setValue) {
  const { value } = e.target;
  setValue(value);
}

export function onChangeNumber(e, setValue) {
  const { value } = e.target;
  const number = Number(value);

  if ((number && value.length < 10) || number === 0 || value === '') {
    setValue(value);
  }
}

export function onChangeRate(e, setValue) {
  const { value } = e.target;

  if (value === '' || (Number(value) >= 0 && Number(value) <= 5)) {
    setValue(value);
  }
}

export function onChangeChecked(e, setChecked, onSave = () => {}) {
  const { checked } = e.target;
  setChecked(checked);
  onSave();
}

export const clearUrl = (url) => {
  if (!url) {
    return '';
  }
  return url.replace('https://', '').replace('http://', '').replace('www.', '').replace('tel:', '').replace('mailto:', '').replace('viber://chat?number=', '');
};

export function onClickOpenSite(link, linkType) {
  let linkResult;
  if (link) {
    linkResult = link.startsWith('www.') ? link.replace('www.', 'http://') : link;
  }
  if (linkType === 'email') {
    linkResult = link.startsWith('www.') ? link.replace('www.', 'http://mailto:') : link;
  }
  if (linkType === 'phone') {
    linkResult = link.startsWith('www.') ? link.replace('www.', 'http://tel:') : link;
  }
  window.open(linkResult, '_blank');
}

export function selectOnFocus(event) {
  event.target.select();
}

export function runWfpWdgt(url) {
  const wayforpay = new window.Wayforpay();

  wayforpay.invoice(url);
}

// type: 'donation',
//   id,
//   login: wayforpayMerchantLogin,
//
//   userId,
//   currencyCode: getSupportedCurrency('WayForPay', currencyCode),
//
//   sum,
//   price: donationPrice,
//   name: donationType === 'donations' ? donationTitle : donationTitle,
//   count,

export function runWfpSign(props) {
  const { username, email: userEmail, type, success = () => {}, onReady = () => {}, onError = () => {} } = props;

  const firstName = username || 'User';
  const lastName = '';
  const email = userEmail || 'noreply@unilink.us';
  const phone = '380670000000';

  if (!window.Wayforpay) {
    console.warn('WayForPay widget not loaded yet, retrying...');
    const script = document.createElement('script');
    script.src = 'https://secure.wayforpay.com/server/pay-widget.js';
    script.onload = () => {
      if (window.Wayforpay) {
        runWfpSign(props);
      } else {
        alert('Payment system is not loaded. Please refresh the page and try again.');
        onError();
      }
    };
    script.onerror = () => {
      alert('Payment system is not loaded. Please refresh the page and try again.');
      onError();
    };
    document.head.appendChild(script);
    return;
  }

  const wayforpay = new window.Wayforpay();

  httpClient
    .post('api/subscriptions/wayforpay/checkout', {
      planType: type,
      isRecurring: true,
    })
    .then((res) => {
      if (res && res.data && res.data.merchantSignature) {
        const checkoutData = res.data;
        onReady();

        wayforpay.run(
          {
            merchantTransactionSecureType: 'AUTO',
            merchantAccount: checkoutData.merchantAccount,
            merchantDomainName: checkoutData.merchantDomainName,
            authorizationType: 'SimpleSignature',
            merchantSignature: checkoutData.merchantSignature,
            orderReference: checkoutData.orderReference,
            orderDate: checkoutData.orderDate,
            amount: checkoutData.amount,
            currency: checkoutData.currency,
            productName: checkoutData.productName,
            productPrice: checkoutData.productPrice,
            productCount: checkoutData.productCount,
            clientFirstName: firstName,
            clientLastName: lastName,
            clientEmail: email,
            clientPhone: phone,
            language: 'AUTO',
            straightWidget: false,
            returnUrl: `${BaseUrl}/website`,
            serviceUrl: checkoutData.serviceUrl,
            regularOn: 1,
            regularMode: checkoutData.regularMode,
            regularAmount: checkoutData.regularAmount,
            regularBehavior: checkoutData.regularBehavior,
            dateNext: checkoutData.dateNext,
            dateEnd: checkoutData.dateEnd,
            paymentSystems: 'card;googlePay;applePay',
          },
          function (response) {
            wayforpay.closeit();
            success(response.orderReference, '');
          },
          function () {
            wayforpay.closeit();
          },
        );
      } else {
        console.error('WayForPay checkout API returned unexpected response:', res);
        onError();
      }
    })
    .catch((err) => {
      console.error('WayForPay checkout API error:', err);
      onError();
    });
}

export const renderTooltip = (text) => {
  return (
    <span className='css-tooltip' data-tooltip={text}>
      <svg fill='#6c6c6c' height='10px' width='10px' viewBox='0 0 512 512'>
        <path d='m277.332031 384c0 11.78125-9.550781 21.332031-21.332031 21.332031s-21.332031-9.550781-21.332031-21.332031 9.550781-21.332031 21.332031-21.332031 21.332031 9.550781 21.332031 21.332031zm0 0' />
        <path d='m256 512c-141.164062 0-256-114.835938-256-256s114.835938-256 256-256 256 114.835938 256 256-114.835938 256-256 256zm0-480c-123.519531 0-224 100.480469-224 224s100.480469 224 224 224 224-100.480469 224-224-100.480469-224-224-224zm0 0' />
        <path d='m256 314.667969c-8.832031 0-16-7.167969-16-16v-21.546875c0-20.308594 12.886719-38.507813 32.042969-45.269532 25.492187-8.980468 42.625-36.140624 42.625-55.851562 0-32.363281-26.304688-58.667969-58.667969-58.667969s-58.667969 26.304688-58.667969 58.667969c0 8.832031-7.167969 16-16 16s-16-7.167969-16-16c0-49.984375 40.664063-90.667969 90.667969-90.667969s90.667969 40.683594 90.667969 90.667969c0 35.585938-28.097657 73.367188-63.980469 86.039062-6.398438 2.238282-10.6875 8.316407-10.6875 15.101563v21.527344c0 8.832031-7.167969 16-16 16zm0 0' />
      </svg>
    </span>
  );
};

export const renderCaptionTooltip = (caption, tooltip, captionBold = false, captionColor = null, position = 'static', fontSize = '', margin = '0') => {
  return (
    <CaptionContainer position={position} margin={margin}>
      <Caption fontSize={fontSize} bold={captionBold} color={captionColor}>
        {caption}
      </Caption>
      <S.QuestionBox>{renderTooltip(tooltip)}</S.QuestionBox>
    </CaptionContainer>
  );
};

export const renderCaption = ({ caption, tooltip, captionBold = false, captionColor = null, position = 'static', fontSize = '', margin = '0' }) => {
  return (
    <CaptionContainer position={position} margin={margin}>
      <Caption fontSize={fontSize} bold={captionBold} color={captionColor}>
        {caption}
      </Caption>
      <S.QuestionBox>{renderTooltip(tooltip)}</S.QuestionBox>
    </CaptionContainer>
  );
};

export const renderTitleTooltip = (title, tooltip, relativeTooltip = false) => {
  return (
    <>
      {title}
      <S.QuestionBox inline relativeTooltip={relativeTooltip}>
        {renderTooltip(tooltip, relativeTooltip)}
      </S.QuestionBox>
    </>
  );
};

export const animationRender = (bounce, markup) => {
  switch (bounce) {
    case 'pulse': {
      return <S.Pulse>{markup}</S.Pulse>;
    }
    case 'shake': {
      return <S.Shake>{markup}</S.Shake>;
    }
    case 'tada': {
      return <S.Tada>{markup}</S.Tada>;
    }
    case 'bounceIn': {
      return <S.BounceIn>{markup}</S.BounceIn>;
    }
    case 'fadeIn': {
      return <S.FadeIn>{markup}</S.FadeIn>;
    }
    case 'flipInX': {
      return <S.FlipInX>{markup}</S.FlipInX>;
    }
    case 'flipInY': {
      return <S.FlipInY>{markup}</S.FlipInY>;
    }
    case 'zoomIn': {
      return <S.ZoomIn>{markup}</S.ZoomIn>;
    }
    case 'swing': {
      return <S.Swing>{markup}</S.Swing>;
    }
    case 'jello': {
      return <S.Jello>{markup}</S.Jello>;
    }
    default: {
      return markup;
    }
  }
};

export function getMediaHeaderType(data, headerTypeCurrent, activeImagePreview, youTubeLink, activeGifPreview) {
  let mediaType = 0;
  let mediaUrl = '';

  switch (headerTypeCurrent) {
    case 'Image':
      mediaType = 1;
      mediaUrl = activeImagePreview;
      break;
    case 'Video':
      mediaType = 2;
      mediaUrl = youTubeLink;
      break;
    case 'Gif':
      mediaType = 3;
      mediaUrl = activeGifPreview;
      break;
    case 'Hero':
      mediaType = 5;
      break;
    default:
      mediaType = 1;
      mediaUrl = '';
      break;
  }

  if (data === 'type') {
    return mediaType;
  } else if (data === 'url') {
    return mediaUrl;
  } else {
    return {
      mediaType,
      mediaUrl,
    };
  }
}

export function updateDropdownItem(
  event,
  { typeAPI, typesList, itemsList, setCurrentItem, setTypeItems, typesListArray, isNotAllList, updateItemId = () => {}, updatePageId = () => {} },
) {
  let newCurrentItem;

  if (event) {
    const item = event.target;
    let index = item.getAttribute('data-index');

    if (!index) {
      index = item.parentNode.getAttribute('data-index');
    }
    updateItemId(index);
    newCurrentItem = itemsList[index];
    updatePageId(newCurrentItem);
  } else if (typeAPI) {
    newCurrentItem = typeAPI;
  }

  let typesValuesList;

  if (!typesListArray) {
    typesValuesList = Object.values(typesList);
  } else {
    typesValuesList = typesListArray;
  }

  if (typesValuesList.includes(newCurrentItem)) {
    setCurrentItem(newCurrentItem);
    if (isNotAllList) {
      setTypeItems(typesValuesList.filter((el) => el !== newCurrentItem));
    }
  } else {
    setCurrentItem(typesValuesList[0]);
    setTypeItems(typesValuesList.slice(1));
    if (isNotAllList) {
      setTypeItems(typesValuesList.slice(1));
    }
  }
}

export function getMainCurrency(currencyCode) {
  switch (currencyCode) {
    case 'USD':
      return '$';
    case 'EUR':
      return '€';
    case 'GBP':
      return '£';
    case 'UAH':
      return 'грн';
    case 'RUB':
      return '₽';
    case 'AED':
      return 'AED';
    case 'BRL':
      return 'R$';
    case 'INR':
      return '₹';
    case 'PLN':
      return 'zł';
    case 'CZK':
      return 'Kč';
    default:
      return '$';
  }
}

export function getPrice(price, currency) {
  if (!price) {
    price = 0;
  }
  switch (currency) {
    case '$':
      return `$${price}`;
    case '€':
      return `${price} €`;
    case '£':
      return `£${price}`;
    case 'грн':
      return `${price} грн`;
    case 'zł':
      return `${price} zł`;
    case 'Kč':
      return `${price} Kč`;
    case '₽':
      return `₽${price}`;
    case 'AED':
      return `AED ${price}`;
    case 'R$':
      return `R$${price}`;
    case '₹':
      return `₹${price}`;
    default:
      return `$${price}`;
  }
}

// Universal lang resolver: accepts both display names ("English", "Українська")
// and codes ("en", "ua", "uk"). Treats "ua" and "uk" as the same (Ukrainian).
function resolvePreviewLang(input) {
  switch (
    String(input || '')
      .trim()
      .toLowerCase()
  ) {
    case 'en':
    case 'english':
      return enLang;
    case 'es':
    case 'spanish':
      return esLang;
    case 'pt':
    case 'portuguese':
      return ptLang;
    case 'fr':
    case 'french':
      return frLang;
    case 'de':
    case 'german':
      return deLang;
    case 'it':
    case 'italian':
      return itLang;
    case 'tr':
    case 'turkish':
      return trLang;
    case 'ua':
    case 'uk':
    case 'українська':
      return uaLang;
    default:
      return enLang;
  }
}

export function getNewLang(translation) {
  return resolvePreviewLang(translation);
}

export function getLang(translation) {
  return resolvePreviewLang(translation);
}

// Normalize any lang input (code or name, case-insensitive, "ua"/"uk" both map to Ukrainian)
// to its frontend canonical code. Frontend uses "ua" (legacy convention; backend uses "uk" — both accepted).
export function getLangCode(translation) {
  switch (
    String(translation || '')
      .trim()
      .toLowerCase()
  ) {
    case 'en':
    case 'english':
      return 'en';
    case 'es':
    case 'spanish':
      return 'es';
    case 'pt':
    case 'portuguese':
      return 'pt';
    case 'fr':
    case 'french':
      return 'fr';
    case 'de':
    case 'german':
      return 'de';
    case 'it':
    case 'italian':
      return 'it';
    case 'tr':
    case 'turkish':
      return 'tr';
    case 'ua':
    case 'uk':
    case 'українська':
      return 'ua';
    case 'pl':
    case 'polish':
      return 'pl';
    case 'zh':
    case 'zh-cn':
    case 'chinese':
      return 'zh';
    case 'ja':
    case 'japanese':
      return 'ja';
    case 'hu':
    case 'hungarian':
      return 'hu';
    default:
      return 'en';
  }
}

// Deep-merges a language object over the English base so any key missing from a
// translation falls back to its English text instead of rendering as undefined.
function mergeWithEnFallback(langDashboard) {
  if (langDashboard === enLangDashboard) return enLangDashboard;
  const merge = (base, override) => {
    if (!override || typeof override !== 'object' || Array.isArray(override)) {
      return override === undefined ? base : override;
    }
    const out = Array.isArray(base) ? [...base] : { ...base };
    for (const key of Object.keys(override)) {
      const baseVal = base ? base[key] : undefined;
      const overVal = override[key];
      out[key] =
        baseVal && typeof baseVal === 'object' && !Array.isArray(baseVal) && overVal && typeof overVal === 'object' && !Array.isArray(overVal) ? merge(baseVal, overVal) : overVal;
    }
    return out;
  };
  return merge(enLangDashboard, langDashboard);
}

export function updateLanguageDashboard(lang) {
  switch (
    String(lang || '')
      .trim()
      .toLowerCase()
  ) {
    case 'en':
    case 'english':
      return enLangDashboard;
    case 'es':
    case 'spanish':
      return mergeWithEnFallback(esLangDashboard);
    case 'pt':
    case 'portuguese':
      return mergeWithEnFallback(ptLangDashboard);
    case 'fr':
    case 'french':
      return mergeWithEnFallback(frLangDashboard);
    case 'de':
    case 'german':
      return mergeWithEnFallback(deLangDashboard);
    case 'it':
    case 'italian':
      return mergeWithEnFallback(itLangDashboard);
    case 'tr':
    case 'turkish':
      return mergeWithEnFallback(trLangDashboard);
    case 'ua':
    case 'uk':
    case 'українська':
      return mergeWithEnFallback(uaLangDashboard);
    case 'pl':
    case 'polish':
      return mergeWithEnFallback(plLangDashboard);
    case 'zh':
    case 'zh-cn':
    case 'chinese':
      return mergeWithEnFallback(zhLangDashboard);
    case 'ja':
    case 'japanese':
      return mergeWithEnFallback(jaLangDashboard);
    case 'hu':
    case 'hungarian':
      return mergeWithEnFallback(huLangDashboard);
    default:
      return enLangDashboard;
  }
}

export function getLangFromCode(translation) {
  switch (
    String(translation || '')
      .trim()
      .toLowerCase()
  ) {
    case 'en':
    case 'english':
      return 'English';
    case 'es':
    case 'spanish':
      return 'Spanish';
    case 'pt':
    case 'portuguese':
      return 'Portuguese';
    case 'fr':
    case 'french':
      return 'French';
    case 'de':
    case 'german':
      return 'German';
    case 'it':
    case 'italian':
      return 'Italian';
    case 'tr':
    case 'turkish':
      return 'Turkish';
    case 'ua':
    case 'uk':
    case 'українська':
      return 'Українська';
    case 'pl':
    case 'polish':
      return 'Polski';
    case 'zh':
    case 'zh-cn':
    case 'chinese':
      return '中文';
    case 'ja':
    case 'japanese':
      return '日本語';
    case 'hu':
    case 'hungarian':
      return 'Magyar';
    default:
      return 'English';
  }
}

export function toggleShowingValue(value, setIsShowing) {
  setIsShowing(!value);
}

export function copyValue(value) {
  navigator.clipboard.writeText(value);
}

export function toUpperCaseFirstLetter(value) {
  if (typeof value === 'string') {
    return value[0].toUpperCase() + value.slice(1);
  } else return value;
}

export function toLowerCaseFirstLetter(value) {
  if (typeof value === 'string') {
    return value[0].toLowerCase() + value.slice(1);
  } else return value;
}

export function renderTablerIcon(icon, color = '#979b9f') {
  const Icon = getTablerIconSync(icon);
  if (!Icon) {
    loadTablerIcons();
    return null;
  }
  return Icon({ size: 40, color });
}

export function renderHeaderImage(imgLink, insideHeader = false, darkness = null) {
  const isUpload = imgLink?.includes('upload');

  return <S.PreviewImage insideHeader={insideHeader} darkness={darkness} src={isUpload ? `${fixPrefixOnImageName(imgLink, 'gl_')}` : `${imgLink}`} alt='header image' />;
}

export function checkImageIsSvgOrGif(image) {
  if (image) {
    const posSvg = image?.length - 4;
    const svgName = image?.slice(posSvg);

    return svgName === '.svg' || svgName === '.gif';
  }
}

export function fixPrefixOnImageName(imgLink, prefix) {
  if (!imgLink) {
    return '';
  }
  if (
    (imgLink?.includes('.gif') || imgLink?.includes('noproduct.png') || imgLink?.includes('.svg') || imgLink?.includes('unsplash.com') || imgLink?.includes(prefix)) &&
    prefix !== ''
  ) {
    return imgLink;
  }

  if (imgLink && (imgLink?.includes('cdn.unil.ink') || imgLink?.includes('unilink.us'))) {
    let imageName = '';

    const splitLink = imgLink?.split('/');
    const indexImgName = splitLink.length - 1;
    const fullImageName = splitLink[indexImgName];

    const splitImageName = imgLink?.split('_');

    if (splitImageName.length > 1) {
      imageName = splitImageName[1];
    } else {
      imageName = fullImageName;
    }

    return `${splitLink.slice(0, indexImgName).join('/')}/${prefix}${imageName}`;
  }

  return imgLink;
}

export function changePrefixShopImage(imgLink, layout) {
  if (!imgLink) {
    return;
  }

  if (imgLink.includes('.gif') || imgLink.includes('noproduct.png') || imgLink?.includes('.svg') || imgLink?.includes('unsplash.com')) {
    return imgLink;
  }

  let prefix;

  switch (layout) {
    case 1:
      prefix = 'ss_';
      break;
    case 2:
      prefix = 'ms_';
      break;
    case 3:
      prefix = 'ss_';
      break;
    case 4:
      prefix = 'gm_';
      break;
    case 5:
      prefix = 'gm_';
      break;
    case 6:
      prefix = 'ms_';
      break;
    case 7:
      prefix = 'gm_';
      break;
    default:
      prefix = 'ss_';
  }

  if (imgLink.includes('_')) {
    const splitLink = imgLink.split('/');
    const indexImgName = splitLink.length - 1;
    const imgName = splitLink[indexImgName].slice(3);

    return `${splitLink.slice(0, indexImgName).join('/')}/${prefix}${imgName}`;
  } else {
    const splitLink = imgLink.split('/');
    const indexImgName = splitLink.length - 1;
    const imgName = splitLink[indexImgName];

    return `${splitLink.slice(0, indexImgName).join('/')}/${prefix}${imgName}`;
  }
}

export function renderTextAlignIcon(title) {
  switch (title) {
    case 'left':
      return <IconAlignLeft />;
    case 'center':
      return <IconAlignCenter />;
    case 'right':
      return <IconAlignRight />;
    default:
      return;
  }
}

export function renderLinkTypeIcon(title, color) {
  switch (title) {
    case buttonLinkUrlType.link:
    case buttonLinkUrlCaptions.link:
      return <IconLinkTypeSVG color={color ?? '#000000'} />;
    case buttonLinkUrlCaptions.skype:
    case buttonLinkUrlType.skype:
      return <IconSkypeTypeSVG color={color ?? '#000000'} />;
    case buttonLinkUrlCaptions.viber:
    case buttonLinkUrlType.viber:
      return <IconViberTypeSVG color={color ?? '#000000'} />;
    case buttonLinkUrlCaptions.email:
    case buttonLinkUrlType.email:
      return <IconEmailTypeSVG color={color ?? '#000000'} />;
    case buttonLinkUrlCaptions.phone:
    case buttonLinkUrlType.phone:
      return <IconPhoneTypeSVG color={color ?? '#000000'} />;
    case buttonLinkUrlCaptions.page:
    case buttonLinkUrlType.page:
      return <IconPageLinkSVG color={color ?? '#000000'} />;
    case buttonLinkUrlCaptions.post:
    case buttonLinkUrlType.post:
      return <IconPostLinkSVG color={color ?? '#000000'} />;
    case buttonLinkUrlCaptions.document:
    case buttonLinkUrlType.document:
      return <IconDocumentLinkSVG color={color ?? '#000000'} />;
    case statusTypes.avaliable:
      return <IconAvaliableSVG />;
    case statusTypes.soldout:
      return <IconSoldoutSVG />;
    default:
      return;
  }
}

export function renderContactsIcon(title) {
  const typeTitle = title?.toLowerCase();

  switch (typeTitle) {
    case 'facebook messenger':
      return <IconContactMessengerSVG />;
    case 'whatsapp':
      return <IconContactWhatsAppSVG />;
    case 'viber':
      return <IconViberTypeSVG />;
    case 'telegram':
      return <IconContactTelegramSVG />;
    case 'twitter':
      return <IconContactTwitterSVG />;
    case 'phone':
      return <IconPhoneTypeSVG />;
    case 'email':
      return <IconEmailTypeSVG />;
    case 'location':
      return <IconLinkTypeSVG />;
    case 'instagram':
      return <IconContactInstagramSVG />;
    default:
      return;
  }
}

export function renderContactImage(title) {
  const typeTitle = title?.toLowerCase();

  let icon;

  switch (typeTitle) {
    case 'facebook messenger':
      icon = `facebook-messenger.png`;
      break;
    case 'whatsapp':
      icon = `whatsapp.png`;
      break;
    case 'viber':
      icon = `viber.png`;
      break;
    case 'telegram':
      icon = `telegram.png`;
      break;
    case 'twitter':
      icon = `twitter.png`;
      break;
    case 'phone':
      icon = `phone.png`;
      break;
    case 'email':
      icon = `email.png`;
      break;
    case 'location':
      icon = `location.png`;
      break;
    case 'instagram':
      icon = `instagram.png`;
      break;
    case 'skype':
      icon = `skype.png`;
      break;
    case 'linkedin':
      icon = `linkedin.png`;
      break;
    case 'calendly':
      icon = `calendly.png`;
      break;
    case 'link':
      icon = `link.png`;
      break;
    case 'instagram direct':
      icon = `instagram.png`;
      break;
    case 'facebook':
      icon = `facebook.png`;
      break;
    case 'x':
      icon = `x.png`;
      break;
    case 'youtube':
      icon = `youtube.png`;
      break;
    case 'tiktok':
      icon = `tiktok.png`;
      break;
    case 'spotify':
      icon = `spotify.png`;
      break;

    default:
      return;
  }

  return <S.ContactBox isDropDownImage src={`https://unilink.us/content/social/${icon}`} alt='icon' />;
}

export function renderLangIcon(title) {
  const typeTitle = title?.toLowerCase();

  switch (typeTitle) {
    case 'українська':
      return <span className='fi fi-ua' />;
    case 'english':
      return <span className='fi fi-gb' />;
    case 'spanish':
      return <span className='fi fi-es' />;
    case 'portuguese':
      return <span className='fi fi-pt' />;
    case 'french':
      return <span className='fi fi-fr' />;
    case 'german':
      return <span className='fi fi-de' />;
    case 'italian':
      return <span className='fi fi-it' />;
    case 'turkish':
      return <span className='fi fi-tr' />;
    case 'русский (stop war)':
      // return (
      //   // <span className="fi fi-ru" />
      //
      // )
      return 'RU';
    default:
      return;
  }
}

export function checkApple() {
  return (
    ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(window.navigator.platform) ||
    // iPad on iOS 13 detection
    window.navigator.userAgent.includes('Mac')
  );
}

export function renderContacts(contacts, isFirst = false, facebookPixel, isInsideDashboard, userId, isActiveButton = false, isList = false) {
  let renderContactsList = [];

  if (isFirst) {
    renderContactsList = contacts.slice(0, 1);
  } else {
    renderContactsList = contacts.slice(1);
  }

  return (
    <UserContactList isList={isList} isActiveButton={isActiveButton} insideBio={!isFirst} isInsideDashboard={isInsideDashboard} directMessage={isFirst} padding='10px 0 0 0'>
      {renderContactsList.map((el) => {
        let { id, url, icon, backgroundColor } = el;

        const title = getIconTitle(icon);

        return (
          <UserContactItemLink
            insideBio
            column={!isFirst ? contacts.length !== 2 : null}
            center
            key={id}
            background={backgroundColor}
            // href={Url}
            onClick={(e) => {
              e.preventDefault();
              if (facebookPixel) {
                window.fbq('track', 'Contact');
              }

              window.open(`${url}`, 'blank_');
            }}
          >
            <UserContactIcon src={`https://unilink.us/content/social/${icon}`} loading='lazy' alt={title} />

            {isFirst && <S.UserContactTitle>{title}</S.UserContactTitle>}

            {!isFirst && contacts.length === 2 && <S.UserContactTitle>{title}</S.UserContactTitle>}
          </UserContactItemLink>
        );
      })}
    </UserContactList>
  );
}

export function parseMediaList(list) {
  let parsedList = [];

  if (Array.isArray(list)) {
    list.forEach((el) => {
      if (el?.Id) {
        parsedList.push(getUnlinkItemMap(el));
      } else if (el?.id) {
        parsedList.push(el);
      }
    });
    return parsedList;
  }
}

export function parseYouTubeList(list) {
  let parsedList = [];

  if (Array.isArray(list)) {
    list.forEach((el) => parsedList.push(getYouTubeMediaItemMap(el)));
  }

  return parsedList;
}

export function createCategoriesOptions(categories, setCategoriesOptions, currentItemCategories = []) {
  let newCategoriesOptions = [
    {
      value: 'none',
      label: 'none',
    },
  ];

  if (categories.length) {
    const disabledIds = currentItemCategories.length > 0 ? new Set(currentItemCategories.map((el) => el.categoryId ?? el.id)) : new Set();
    const titleById = new Map(categories.map((c) => [c.id, c.title]));

    const roots = [];
    const childrenByParent = new Map();
    categories.forEach((c) => {
      if (c.parentId && titleById.has(c.parentId)) {
        const arr = childrenByParent.get(c.parentId) ?? [];
        arr.push(c);
        childrenByParent.set(c.parentId, arr);
      } else {
        roots.push(c);
      }
    });

    const pushOption = (cat, parentTitle) => {
      newCategoriesOptions.push({
        value: cat.id,
        label: parentTitle ? `${parentTitle} → ${cat.title}` : cat.title,
        disabled: disabledIds.has(cat.id),
      });
    };

    roots.forEach((root) => {
      pushOption(root, null);
      const children = childrenByParent.get(root.id) ?? [];
      children.forEach((child) => pushOption(child, root.title));
    });
  }

  setCategoriesOptions(newCategoriesOptions);
}

export function parseTrustList(list) {
  if (!Array.isArray(list)) return [];

  return list.map((el) => ({
    Id: el.id,
    Title: el.title,
    Description: el.description,
    Image: el.image,
    IsActive: el.is_active,
    IsCustomDesign: el.is_custom_design,
    TextColor: el.text_color,
    TextAlign: el.text_align,
    BackgroundColor: el.background_color,
    BackgroundRGBA: el.background_rgba,
    GradientColor1: el.gradient_color1,
    GradientColor2: el.gradient_color2,
    GradientDirection: el.gradient_direction,
    GradientOffset: el.gradient_offset,
  }));
}

export function createPropertiesOptions(property, setItemOptions, currentItemList = []) {
  let newOptions = [
    {
      type: 0,
      value: 'none',
      label: 'none',
      color: '',
      disabled: false,
    },
  ];

  if (property && property.Values) {
    const { id, values: items, type } = property;

    let currentItemsIds = [];

    if (currentItemList.length > 0) {
      const currentActiveItems = currentItemList.find((el) => el.id === id);

      if (currentActiveItems) {
        currentItemsIds = currentActiveItems.values.map((el) => el.id);
      }
    }

    items.forEach((el) => {
      const { id, value } = el;
      newOptions.push({
        type,
        value,
        label: value,
        disabled: currentItemsIds.includes(id),
        color: value,
      });
    });
  }

  setItemOptions(newOptions);
}

export function createShopSettingList(categories) {
  return categories?.map((el) => {
    const { id, title, cover, n, provider, parentId, isVisible, description, slug } = el;

    return {
      id,
      title,
      ...(cover && { cover }),
      ...(provider && { provider }),
      ...(parentId != null && { parentId }),
      ...(isVisible != null && { isVisible }),
      ...(description != null && { description }),
      ...(slug && { slug }),
      n,
    };
  });
}

export function sortShopList(list) {
  return list?.slice()?.sort((prevSection, nextSection) => (prevSection.N < nextSection.n ? 1 : -1));
}

// export function sortItems(items) {
//   if (!items) {
//     return [];
//   }

//   return items?.slice()?.sort((prev, next) => (prev?.n > next?.n ? 1 : -1));
// }
export function sortItems(items) {
  if (!Array.isArray(items)) return [];
  return items.slice().sort((prev, next) => (prev.n > next.n ? 1 : -1));
}

export function sortItemsForward(items) {
  if (!items) {
    return [];
  }

  return items?.slice()?.sort((prev, next) => (prev.n < next.n ? 1 : -1));
}

export function updateNumberList(updatedList) {
  return updatedList.map((el, i) => {
    return {
      ...el,
      n: i + 1,
    };
  });
}

/**
 * @param {number} indexOffset How many items precede `list` in the full collection. Non-zero only
 *   when a paginated view hands over one page: positions must stay unique across the whole list,
 *   and numbering each page from 1 would give every page the same set.
 */
export function onDragEnd(result, list, indexOffset = 0) {
  const { destination, source } = result;

  if (!destination) {
    return;
  }

  if (destination.droppableId === source.droppableId && destination.index === source.index) {
    return;
  }

  const { index: indexFrom } = source;
  const { index: indexTo } = destination;

  let updatedDragList = [...list];

  updatedDragList.splice(indexFrom, 1);
  updatedDragList.splice(indexTo, 0, list[indexFrom]);

  const newDragList = updatedDragList.map((el, i) => {
    return {
      ...el,
      n: indexOffset + i + 1,
    };
  });

  const listNumbers = newDragList.map((el) => {
    const { id, n } = el;

    return {
      id,
      n,
    };
  });

  return {
    updatedList: newDragList,
    listNumbers,
  };
}

export function parseCustomFields(customFields) {
  if (customFields && Array.isArray(customFields)) {
    return customFields.map((el) => {
      const { customFormId, id, isChecked, isRequired, name, options, placeholder, type } = el;

      return {
        ...(customFormId && { customFormId }),
        id: id,
        isChecked,
        isRequired,
        name,
        options,
        placeholder,
        type,
      };
    });
  } else {
    return [];
  }
}

export function scrollToTop() {
  window.scrollTo(0, 0);
}

export function checkIOS() {
  return (
    ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(navigator.platform) ||
    // iPad on iOS 13 detection
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
  );
}

export function checkIsIPhone() {
  const { appVersion } = window.navigator;
  return appVersion.includes('iPhone') || appVersion.toLowerCase().includes('iphone');
}

export function getActiveLabelsMap(list) {
  return list.map((item) => {
    const { id, labelId, mediaId, color, text } = item;

    return {
      id,
      labelId,
      mediaId,
      color,
      text,
    };
  });
}

export function connectPersonalInstagram() {
  window.location =
    'https://api.instagram.com/oauth/authorize?client_id=1189268101465376&redirect_uri=https://unilink.us/instagram/callback&scope=user_profile,user_media&response_type=code';
}

export function connectBusinessInstagram() {
  const scope = 'pages_show_list,pages_read_engagement,instagram_basic';
  const fbLoginData = { scope };

  try {
    window.FB.getLoginStatus((response) => {
      console.log('facebook login status', response);
    });

    window.FB.login((resp) => {
      if (resp.authResponse) {
        const redirectLoc = `${BaseUrl}/igfacebook/callback`;
        const { accessToken, userID } = resp.authResponse;
        window.location = `${redirectLoc}${redirectLoc.indexOf('?') > -1 ? '&' : '?'}auth=facebook&accessToken=${accessToken}&userAppId=${userID}&scope=${scope}&ulToken=${encodeURIComponent(getToken() || '')}`;
      } else {
        alert('User cancelled login or did not fully authorize.');
      }
    }, fbLoginData);
  } catch (error) {
    console.log('FB login error', error.toString());
    console.log(error);
  }
}

export function getThinSocialDirectIcon(type) {
  switch (type) {
    case 1:
      return IconDirectDefault;
    case 2:
      return IconDirectMessenger;
    case 3:
      return IconDirectWhatsApp;
    case 4:
      return IconDirectViber;
    case 5:
      return IconDirectTelegram;
    case 6:
      return IconDirectPhone;
    case 7:
      return IconDirectEmail;
    case 8:
      return IconDirectInstagram;
  }
}

export function getThinSocialDirectSVGIcon(title, color) {
  switch (title) {
    case 'Messenger':
      return <IconDirectMessengerSVG color={color} />;
    case 'WhatsApp':
      return <IconDirectWhatsAppSVG color={color} />;
    case 'Viber':
      return <IconDirectViberSVG color={color} />;
    case 'Telegram':
      return <IconDirectTelegramSVG color={color} />;
    case 'Phone':
      return <IconDirectPhoneSVG color={color} />;
    case 'Email':
      return <IconDirectEmailSVG color={color} />;
    case 'Instagram':
      return <IconDirectInstagramSVG color={color} />;
    default:
      return null;
  }
}

export const formattedDate = (date) => {
  if (!date) {
    return '';
  }
  return moment(date).locale('en').format('MMM, D');
};

const units = ['bytes', 'KB', 'MB', 'GB', 'TB', 'PiB', 'EiB', 'ZiB', 'YiB'];

export function parseBytes(x) {
  let l = 0,
    n = parseInt(x, 10) || 0;

  while (n >= 1024 && ++l) {
    n = n / 1024;
  }

  return n.toFixed(n < 10 && l > 0 ? 1 : 0) + ' ' + units[l];
}

export const getItemsNameByType = (type) => {
  switch (type) {
    case sectionTypes.links.type:
      return 'links';
    case sectionTypes.digital.type:
      return 'products';
    case sectionTypes.courses.type:
      return 'courses';
    case sectionTypes.faq.type:
      return 'faqs';
    case sectionTypes.shop.type:
      return 'custom';
    case sectionTypes.wallet.type:
      return 'wallets';
    case sectionTypes.youtube.type:
      return 'youtube';
    case sectionTypes.contacts.type:
      return 'contacts';
    case sectionTypes.instagram.type:
      return 'mediaex';
    default:
      return 'items';
  }
};

export const removeTextMarkup = (data) => {
  if (!data) {
    return '';
  }
  return data?.replace(/<\/?[^>]+>/gi, '');
};

export const getDaysDifference = (date, localization = 'en') => {
  if (!date) {
    return '';
  }
  return moment(date).locale(localization).fromNow();
};

export const blocksDataToSave = (blocks) =>
  blocks.map((el) => {
    const { id, sid, uid, pageId, profileId, isActive, type, title, n } = el;
    return {
      id,
      sid,
      uid,
      pageId,
      profileId,
      isActive,
      type,
      title,
      n,
    };
  });

export const sortById = (list) => {
  //TODO fix fucntion
  if (!list.length) {
    return;
  }

  return list.sort(function (a, b) {
    if (Number(a.id) > Number(b.id)) {
      return 1;
    }
    if (Number(a.id) < Number(b.id)) {
      return -1;
    }
    return 0;
  });
};

export const replaceMsToGl = (url) => (url ? url.replace('ms_', 'gl_') : url);

export const replaceMsToGm = (url, prefix = 'gm_') => (url ? url.replace('ms_', prefix) : url);

export const convertDurationStringToSeconds = (input) => {
  if (input == null || input === '') return 0;
  if (typeof input === 'number') return Number.isFinite(input) ? input : 0;
  const str = String(input).trim();
  if (/^\d+(\.\d+)?$/.test(str)) return Number(str);
  if (/^\d+(:\d+){1,2}(\.\d+)?$/.test(str)) {
    return str
      .split(':')
      .map(Number)
      .reduce((acc, val) => acc * 60 + val, 0);
  }
  return 0;
};

export function normalizeExternalUrl(url) {
  if (!url) return url;
  if (/^[a-zA-Z][a-zA-Z0-9+\-.]*:/.test(url)) return url;
  if (url.startsWith('//')) return url;
  if (/\s/.test(url)) return url;
  const host = url.split(/[/?#:]/)[0];
  const labels = host.split('.');
  if (labels.length < 2) return url;
  const validLabel = /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?$/;
  if (!labels.every((l) => l.length > 0 && validLabel.test(l))) return url;
  const tld = labels[labels.length - 1];
  if (!/^[a-zA-Z]{2,}$/.test(tld)) return url;
  return 'https://' + url;
}
